import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import styles from '../styles/ConsolePage.module.css';
import '../styles/design-system.css';
import { useRealtimeWebRTC, RealtimeEvent } from '../utils/useRealtimeWebRTC';
import { useUIScroller } from '../utils/useUIScroller';
import { useWebRTCWaveRenderer } from '../utils/useWebRTCWaveRenderer';
import { systemInstructions, defaultSessionConfig, SessionConfig as SessionConfigType } from '../config/realtimeConfig';
import { EventLog } from '../components/EventLog';
import { SessionConfig as SessionConfigComponent } from '../components/SessionConfig';
import { baseTools, createMemoryTools, MemoryDisplay } from '../tools';
import { createVisualizationTools, useVisualization } from '../tools/visualization';
import { Visualization } from '../components/Visualization';
import { ChatSection } from '../components/ChatSection';
import { Landing } from '../components/Landing';
import { loadMemoryState } from '../tools/memory/storage';
import { getMemorySystemInstructions, MemoryState } from '../tools/memory';
import { 
  serializeHistory,
  deserializeHistory,
  toApiMessage, 
  isTextContent, 
  isAudioContent, 
  isFunctionCallContent,
  Message
} from '../types/history';

export function ConsolePage() {
  const startTimeRef = useRef<string>(new Date().toISOString());
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [memoryState, setMemoryState] = useState<MemoryState | undefined>(undefined);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const visualizationState = useVisualization();
  const [sessionConfig, setSessionConfig] = useState<SessionConfigType>(() => ({
    ...defaultSessionConfig,
    voice: 'coral'
  }));

  // Create tools array with memory tools
  const tools = useMemo(() => [
    ...baseTools,
    ...createMemoryTools(),
    ...createVisualizationTools()
  ], []);

  // Load memory state when user changes
  useEffect(() => {
    async function loadMemory() {
      if (currentUserId) {
        try {
          const state = await loadMemoryState(currentUserId);
          setMemoryState(state);
        } catch (error) {
          console.error('Error loading memory:', error);
          setMemoryState(undefined);
        }
      } else {
        setMemoryState(undefined);
      }
    }
    loadMemory();
  }, [currentUserId]);

  // Listen for memory update events
  useEffect(() => {
    const handleMemoryUpdate = (event: CustomEvent<{ userId: string; state: MemoryState }> & { detail: { userId: string; state: MemoryState } }) => {
      const { userId: updatedUserId, state } = event.detail;
      if (updatedUserId === currentUserId) {
        setMemoryState(state);
      }
    };

    window.addEventListener('memory_updated', handleMemoryUpdate as EventListener);
    return () => {
      window.removeEventListener('memory_updated', handleMemoryUpdate as EventListener);
    };
  }, [currentUserId]);

  const { 
    localCanvasRef, 
    remoteCanvasRef,
    remoteAnalyzerRef,
    connectLocalStream, 
    connectRemoteStream 
  } = useWebRTCWaveRenderer();

  const { 
    isConnected, 
    isMuted, 
    setIsMuted, 
    connect: connectConversation, 
    disconnect: disconnectConversation,
    sendTextMessage,
    history,
    setHistory,
    realtimeEvents
  } = useRealtimeWebRTC(
    startTimeRef,
    getMemorySystemInstructions(systemInstructions, currentUserId, memoryState),
    audioRef,
    tools,
    connectLocalStream,
    connectRemoteStream,
    sessionConfig
  );

  // Update session config when loading history, but only when not connected
  useEffect(() => {
    if (history.session_config && !isConnected) {
      setSessionConfig({
        ...defaultSessionConfig,
        ...history.session_config
      });
    }
  }, [history, isConnected]);

  // Expose history for debugging
  useEffect(() => {
    (window as any)._debug_history = history;
    console.log('Debug history updated:', history);
  }, [history]);

  const { scrollRef: eventsScrollRef } = useUIScroller(realtimeEvents);

  const formatTime = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }, []);

  const [isMaximized, setIsMaximized] = useState(false);
  const [maximizedSection, setMaximizedSection] = useState<'memory' | 'events' | null>(null);

  const handleUserChange = useCallback((userId: string) => {
    setCurrentUserId(userId);
  }, []);

  const handleDisconnect = useCallback(() => {
    setShowLanding(true);
    disconnectConversation();
  }, [disconnectConversation]);

  return (
    <div className={styles.page}>
      {/* Back Button - Always at top left when not on landing */}
      {!showLanding && (
        <button 
          className={`${styles.backButton} ${styles.topLeft}`}
          onClick={() => {
            if (isConnected) {
              handleDisconnect();
            } else {
              setShowLanding(true);
            }
          }}
        >
          ← 
        </button>
      )}

      <div className={styles.visualizationContainer}>
        {isConnected && !showLanding && (
            <Visualization 
              audioAnalyser={remoteAnalyzerRef.current?.analyser}
              backgroundColor={visualizationState?.color}
              customCode={visualizationState?.customCode}
            />
        )}
      </div>

      {!isConnected && showLanding && (
        <Landing 
          onStartChat={() => {
            if (currentUserId) {
              connectConversation();
              setShowLanding(false);
            }
          }}
          onStateChange={(state) => {
            console.log('Landing state changed:', state);
            // Handle other landing states if needed
          }}
        />
      )}

      {/* Config Button (visible when overlay is hidden) */}
      {!showOverlay && (
        <button 
          className={styles.floatingConfig}
          onClick={() => setShowOverlay(true)}
        >
          ⚙
        </button>
      )}

      {/* Overlay Controls & UI */}
      <div className={`${styles.overlay} ${showOverlay ? styles.visible : ''} ${isMaximized ? styles.maximized : ''}`}>
        <header className={styles.overlayHeader}>
          <div className={styles.overlayControls}>
            <button 
              className={styles.overlayToggle}
              onClick={() => setShowOverlay(prev => !prev)}
            >
              {showOverlay ? '×' : '⚙'}
            </button>
            {showOverlay && (
              <button 
                className={styles.overlayToggle}
                onClick={() => setIsMaximized(prev => !prev)}
              >
                {isMaximized ? '⊟' : '⊞'}
              </button>
            )}
          </div>
          <div className={styles.buttonGroup}>
            <button 
              onClick={() => {
                const blob = new Blob([serializeHistory(history)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `conversation-${history.id}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className={styles.btnDark}
              title="Download conversation history"
            >
              💾 Save
            </button>
            <label className={styles.btnDark} title="Load conversation history">
              📂 Load
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      try {
                        const json = event.target?.result as string;
                        const loadedHistory = deserializeHistory(json);
                        if (!isConnected) {
                          setHistory(loadedHistory);
                        } else {
                          console.warn('Cannot load history while connected');
                          alert('Please disconnect before loading a conversation history');
                        }
                      } catch (error) {
                        console.error('Error loading history:', error);
                        alert('Invalid conversation history file');
                      }
                    };
                    reader.readAsText(file);
                  }
                }}
              />
            </label>
            {isConnected ? (
              <>
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className={isMuted ? styles.btnWarning : styles.btnDark}
                >
                  {isMuted ? '🔇 Unmute' : '🔊 Mute'}
                </button>
                <button
                  onClick={handleDisconnect}
                  className={styles.btnDanger}
                >
                  ⏹️ Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  connectConversation();
                  setShowLanding(false);
                }}
                className={styles.btnPrimary}
                disabled={!currentUserId}
              >
                🔌 Connect
              </button>
            )}
          </div>
        </header>

        <div className={styles.overlayContent}>
          {/* Session Config */}
          {!isConnected && (
            <div className="section">
              <div className="section-header">
                <div className="section-title">SESSION CONFIGURATION</div>
              </div>
              <div>
                <SessionConfigComponent 
                  config={sessionConfig}
                  onChange={(newConfig) => {
                    const fullConfig = {
                      ...defaultSessionConfig,
                      ...newConfig
                    };
                    setSessionConfig(fullConfig);
                    setHistory(prev => ({
                      ...prev,
                      session_config: fullConfig
                    }));
                  }}
                  disabled={isConnected}
                />
              </div>
            </div>
          )}

          {/* Messages & Chat */}
          <ChatSection
            history={history}
            setHistory={setHistory}
            isConnected={isConnected}
            currentUserId={currentUserId}
            audioRef={audioRef}
            localCanvasRef={localCanvasRef}
            remoteCanvasRef={remoteCanvasRef}
            sendTextMessage={sendTextMessage}
          />

          {/* Memory Display */}
          <div className="section">
            <div className="section-header">
              <div className="section-title">MEMORY</div>
            </div>
            <div>
              <MemoryDisplay 
                memory={memoryState}
                userId={currentUserId}
                onUserChange={handleUserChange}
              />
            </div>
          </div>

          {/* Debug Events */}
          <div className="section">
            <div className="section-header">
              <div className="section-title">
                <span>DEBUG EVENTS</span>
                <span className="text-sm text-muted">({realtimeEvents.length})</span>
              </div>
            </div>
            <div>
              <div className="events-container">
                <div ref={eventsScrollRef} className="events-list">
                  <EventLog events={realtimeEvents} formatTime={formatTime} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
